import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { buildNavigationStack, type TestType } from "./getNextTestScreen";
import type { TestResultCreate } from "@louise/medical-history/basic-info/types";
import {
  isFertilityTestingResult,
  type FertilityTestingResultLike,
} from "./isFertilityTestingResult";

export interface FertilityTestingState {
  // Patient sex for filtering sex-specific test options
  sex?: "AFAB" | "AMAB" | null;

  // Whether the patient selected None on the consolidated testing question.
  noneSelected?: boolean;

  // Free-text context for a top-level Other testing answer.
  otherTestingNotes?: string | null;

  // Kept for persistence compatibility with existing state and API payloads.
  hasTestingDone?: boolean | null;

  // From FertilityTestingTypesScreen
  selectedTypes?: string[];

  // Single source of truth for test results
  // Objects WITH id = previously saved (need PATCH)
  // Objects WITHOUT id = new (need POST)
  testResults: import("@louise/medical-history/basic-info/types").TestResultCreate[];

  // IDs to delete on submit (when user deselects a type)
  testResultIdsToDelete: string[];

  // Files added through the question-level upload action during this session.
  uploadedFiles: { id: string; name: string }[];

  // Navigation stack (session-based, not persisted)
  navigationStack?: string[];
  currentNavigationIndex?: number;
}

interface FertilityTestingContextType {
  state: FertilityTestingState;
  updateState: (updates: Partial<FertilityTestingState>) => void;
  refreshFromPatient: () => Promise<void>;
  resetState: () => void;
}

const FertilityTestingContext = createContext<
  FertilityTestingContextType | undefined
>(undefined);

const initialState: FertilityTestingState = {
  hasTestingDone: null,
  noneSelected: false,
  otherTestingNotes: null,
  selectedTypes: [],
  testResults: [],
  testResultIdsToDelete: [],
  uploadedFiles: [],
  navigationStack: [],
  currentNavigationIndex: 0,
};

const visualEvidenceUploadedFile = {
  id: "visual-evidence-fertility-testing-upload",
  name: "fertility-testing-report.pdf",
};

let visualEvidenceUploadedFilesLatched = false;

export function latchVisualEvidenceUploadedFiles(enabled: boolean) {
  if (enabled) {
    visualEvidenceUploadedFilesLatched = true;
  }
}

export function hasVisualEvidenceUploadedFiles() {
  return visualEvidenceUploadedFilesLatched;
}

function getVisualEvidenceUploadedFiles() {
  if (process.env.EXPO_PUBLIC_VISUAL_EVIDENCE_UPLOADED_FILES === "true") {
    latchVisualEvidenceUploadedFiles(true);
  }

  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    latchVisualEvidenceUploadedFiles(
      params.get("dev_visual_evidence") === "true" &&
        params.get("dev_uploaded_files") === "true",
    );
  }

  return visualEvidenceUploadedFilesLatched ? [visualEvidenceUploadedFile] : [];
}

function getUploadedFilesFromResults(
  testResults: FertilityTestingState["testResults"],
): { id: string; name: string }[] {
  return testResults
    .filter((result) => Boolean(result.attachmentId))
    .map((result) => ({
      id: result.attachmentId!,
      name: "Uploaded medical report",
    }));
}

export function FertilityTestingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { data: patient, isLoading, refetch } = usePatient();
  const [visualEvidenceUploadedFiles] = useState(
    getVisualEvidenceUploadedFiles,
  );
  const [state, setState] = useState<FertilityTestingState>(() => ({
    ...initialState,
    uploadedFiles: visualEvidenceUploadedFiles,
  }));

  const getTypesFromResults = useCallback(
    (testResults: FertilityTestingResultLike[]): Set<TestType> => {
      const types = new Set<TestType>();
      const typeMap: Record<string, TestType> = {
        urine: "urine",
        blood: "blood",
        genetics: "genetic",
        imaging: "imaging",
        sampling: "sampling",
        semenFluidAnalysis: "semen",
      };

      testResults.forEach((result) => {
        if (!isFertilityTestingResult(result)) {
          return;
        }
        const mappedType = result.type ? typeMap[result.type] : undefined;
        if (mappedType) {
          types.add(mappedType);
        } else if (
          result.type === "clinical" &&
          result.clinicalTestCategory === "other"
        ) {
          types.add("other");
        }
      });

      return types;
    },
    [],
  );

  const refreshFromPatient = useCallback(async () => {
    const { data: refreshedPatient } = await refetch();
    if (!refreshedPatient) {
      return;
    }

    setState((prev) => {
      const serverResults = Array.isArray(refreshedPatient.testResults)
        ? (refreshedPatient.testResults as FertilityTestingState["testResults"])
        : [];
      const deletedIds = new Set(prev.testResultIdsToDelete);
      const localPersistedResults = new Map(
        prev.testResults
          .filter((result) => result.id)
          .map((result) => [result.id, result]),
      );
      const mergedResults = [
        ...serverResults
          .filter((result) => !result.id || !deletedIds.has(result.id))
          .map((result) =>
            result.id && localPersistedResults.has(result.id)
              ? localPersistedResults.get(result.id)!
              : { ...result },
          ),
        ...prev.testResults.filter((result) => !result.id),
      ];
      const selectedTypes = new Set(prev.selectedTypes ?? []);
      const refreshedTypes = getTypesFromResults(mergedResults);
      refreshedTypes.forEach((type) => selectedTypes.add(type));
      const otherResult = mergedResults.find(
        (result) =>
          result.type === "clinical" && result.clinicalTestCategory === "other",
      );
      const hasAnswers = refreshedTypes.size > 0 || selectedTypes.size > 0;
      const serverUploadedFiles = getUploadedFilesFromResults(
        mergedResults as FertilityTestingState["testResults"],
      );
      const uploadedFiles = visualEvidenceUploadedFiles.length
        ? visualEvidenceUploadedFiles
        : [
            ...serverUploadedFiles,
            ...prev.uploadedFiles.filter(
              (file) =>
                !serverUploadedFiles.some((saved) => saved.id === file.id),
            ),
          ];

      return {
        ...prev,
        sex: refreshedPatient.gender,
        hasTestingDone: hasAnswers,
        noneSelected: !hasAnswers,
        selectedTypes: Array.from(selectedTypes),
        otherTestingNotes: otherResult?.notes ?? prev.otherTestingNotes ?? null,
        testResults: mergedResults as TestResultCreate[],
        uploadedFiles,
      };
    });
  }, [getTypesFromResults, refetch, visualEvidenceUploadedFiles]);

  // Load data from patient (like InfertilityContext)
  useEffect(() => {
    // Wait for patient data to load
    if (isLoading) {
      return;
    }

    if (!patient) {
      return;
    }

    const testResults = patient.testResults;

    setState((prev) => {
      // Check if state is in initial empty state - if so, always try to load
      const isInitialState =
        prev.hasTestingDone === null && prev.testResults.length === 0;

      const next: Partial<FertilityTestingState> = {};

      // Always sync sex from patient
      if (patient.gender !== prev.sex) {
        next.sex = patient.gender;
      }

      // Load the consolidated answer (only if empty or initial state)
      if (isInitialState || prev.hasTestingDone === null) {
        if (testResults && getTypesFromResults(testResults).size > 0) {
          next.hasTestingDone = true;
          next.noneSelected = false;
        } else if (Array.isArray(testResults)) {
          next.hasTestingDone = false;
          next.noneSelected = true;
        }
        // If testResults is undefined/null, leave as null (user hasn't answered yet)
      }

      // Load test results ONLY if state is truly empty (initial state)
      // Once user has interacted, don't reload from DB to preserve their changes
      if (isInitialState && testResults && testResults.length > 0) {
        // Load test results directly into state
        next.testResults = testResults.map(
          (tr) =>
            ({
              ...tr,
            }) as import("@louise/medical-history/basic-info/types").TestResultCreate,
        );
        next.uploadedFiles = visualEvidenceUploadedFiles.length
          ? visualEvidenceUploadedFiles
          : getUploadedFilesFromResults(
              testResults as FertilityTestingState["testResults"],
            );

        // Derive selected types from test results
        const selectedTypesArray = Array.from(getTypesFromResults(testResults));
        next.selectedTypes = selectedTypesArray;
        next.noneSelected = selectedTypesArray.length === 0;
        next.otherTestingNotes =
          testResults.find(
            (result) =>
              result.type === "clinical" &&
              result.clinicalTestCategory === "other",
          )?.notes ?? null;
        next.navigationStack = buildNavigationStack(selectedTypesArray);
        next.currentNavigationIndex = 0;
      }

      // Only update if there are changes
      if (Object.keys(next).length === 0) {
        return prev;
      }

      // Merge with previous state (don't replace)
      return { ...prev, ...next };
    });
  }, [getTypesFromResults, isLoading, patient, visualEvidenceUploadedFiles]);

  const updateState = useCallback((updates: Partial<FertilityTestingState>) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  }, []);

  const resetState = useCallback(() => {
    setState({
      ...initialState,
      uploadedFiles: visualEvidenceUploadedFiles,
    });
  }, [visualEvidenceUploadedFiles]);

  return (
    <FertilityTestingContext.Provider
      value={{ state, updateState, refreshFromPatient, resetState }}
    >
      {children}
    </FertilityTestingContext.Provider>
  );
}

export function useFertilityTesting() {
  const context = useContext(FertilityTestingContext);
  if (context === undefined) {
    throw new Error(
      "useFertilityTesting must be used within a FertilityTestingProvider",
    );
  }
  return context;
}
